import {
  addDays,
  addYears,
  compareAsc,
  differenceInCalendarDays,
  differenceInYears,
  format,
  isAfter,
  isBefore,
  isEqual,
  isValid,
  parse,
  parseISO,
  startOfDay,
  subDays,
  subYears,
} from 'date-fns'
import { enCA, frCA } from 'date-fns/locale'
import type { Locale } from 'date-fns'

export { startOfDay }

export function getDateFnsLocale(locale = 'en-CA'): Locale {
  if (locale.startsWith('fr')) {
    return frCA
  }
  return enCA
}

function getLongDatePattern(locale = 'en-CA') {
  if (locale.startsWith('fr')) {
    return 'd MMMM yyyy'
  }
  return 'MMMM d, yyyy'
}

function getSidebarDatePattern(locale = 'en-CA') {
  if (locale.startsWith('fr')) {
    return 'EEE d MMMM yyyy'
  }
  return 'EEE, MMMM d, yyyy'
}

const shortDatePattern = 'yyyy-MM-dd'

export function today() {
  return startOfDay(new Date())
}

export function parsePolicyDate(value?: string | null) {
  if (!value) {
    return null
  }
  var dateOnly = value.match(/^(\d{4}-\d{2}-\d{2})/)?.[1]
  if (dateOnly) {
    var parsed = parse(dateOnly, 'yyyy-MM-dd', today())
    return isValid(parsed) ? startOfDay(parsed) : null
  }
  var parsedIso = parseISO(value)
  return isValid(parsedIso) ? startOfDay(parsedIso) : null
}

export function toLocalDateString(date: Date | null | undefined) {
  if (!date || !isValid(date)) {
    return undefined
  }
  return format(startOfDay(date), shortDatePattern)
}

export function formatLongDate(dateStr?: string | null, locale = 'en-CA') {
  var date = parsePolicyDate(dateStr)
  if (!date) {
    return ''
  }
  return format(date, getLongDatePattern(locale), { locale: getDateFnsLocale(locale) })
}

export function formatLongDateFromDate(date: Date | null | undefined, locale = 'en-CA') {
  if (!date || !isValid(date)) {
    return ''
  }
  return format(date, getLongDatePattern(locale), { locale: getDateFnsLocale(locale) })
}

export function formatShortDate(dateStr?: string | null) {
  var date = parsePolicyDate(dateStr)
  if (!date) {
    return ''
  }
  return format(date, shortDatePattern)
}

export function formatSidebarDate(date: Date | null | undefined, locale = 'en-CA') {
  if (!date || !isValid(date)) {
    return ''
  }
  return format(date, getSidebarDatePattern(locale), { locale: getDateFnsLocale(locale) }).replace(/\./g, '')
}

export function formatLocaleDate(dateStr?: string | null, locale = 'en-CA') {
  var date = parsePolicyDate(dateStr)
  if (!date) {
    return ''
  }
  return format(date, getLongDatePattern(locale), { locale: getDateFnsLocale(locale) })
}

export function formatTodayForFileName() {
  return format(today(), shortDatePattern)
}

export function calendarDaysBetween(end: Date, start: Date) {
  return differenceInCalendarDays(startOfDay(end), startOfDay(start))
}

export function inclusiveTripLength(start: Date, end: Date) {
  return calendarDaysBetween(end, start) + 1
}

export function isOnOrAfter(left: Date, right: Date) {
  return !isBefore(startOfDay(left), startOfDay(right))
}

export function isOnOrBefore(left: Date, right: Date) {
  return !isAfter(startOfDay(left), startOfDay(right))
}

export function isAfterToday(dateStr?: string | null) {
  var date = parsePolicyDate(dateStr)
  if (!date) {
    return false
  }
  return isAfter(date, today())
}

export function isOnOrAfterToday(dateStr?: string | null) {
  var date = parsePolicyDate(dateStr)
  if (!date) {
    return false
  }
  return isOnOrAfter(date, today())
}

export function isSameCalendarDay(left?: string | Date | null, right?: string | Date | null) {
  var leftDate = typeof left === 'string' || left == null ? parsePolicyDate(left) : startOfDay(left)
  var rightDate = typeof right === 'string' || right == null ? parsePolicyDate(right) : startOfDay(right)
  if (!leftDate || !rightDate) {
    return false
  }
  return isEqual(leftDate, rightDate)
}

export function minDateString(values: Array<string | undefined>) {
  var dates = values.map(parsePolicyDate).filter((date): date is Date => date != null)
  if (!dates.length) {
    return undefined
  }
  var earliest = dates.reduce((min, current) => (compareAsc(current, min) < 0 ? current : min))
  return toLocalDateString(earliest)
}

export function addCalendarDays(date: Date, amount: number) {
  return startOfDay(addDays(date, amount))
}

export function addCalendarYears(date: Date, amount: number) {
  return startOfDay(addYears(date, amount))
}

export function subCalendarDays(date: Date, amount: number) {
  return startOfDay(subDays(date, amount))
}

export function subCalendarYears(date: Date, amount: number) {
  return startOfDay(subYears(date, amount))
}

export function annualReturnDate(effectiveDate: Date) {
  return subCalendarDays(addCalendarYears(effectiveDate, 1), 1)
}

export function annualDepartureFromReturn(returnDate: Date) {
  return addCalendarDays(subCalendarYears(returnDate, 1), 1)
}

export function getAgeFromBirthDate(birthDateStr?: string | null) {
  var birthDate = parsePolicyDate(birthDateStr)
  if (!birthDate) {
    return undefined
  }
  return differenceInYears(today(), birthDate)
}

export function isActiveTrip(returnDate?: string | null) {
  return isOnOrAfterToday(returnDate)
}
